import type { SupabaseClient } from '@supabase/supabase-js';

const DEFAULT_CHUNK_SIZE = 15; // Reduced to prevent URL length errors with long treatment names

// Type for objects with id property
interface EntityWithId {
  id: string | number;
}

const normalizeNames = (names: unknown): string[] => [
  ...new Set(
    ((Array.isArray(names) ? names : []) as unknown[])
      .filter((name): name is string => typeof name === 'string')
      .map(name => name.trim())
      .filter(name => name.length > 0)
  ),
];

const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }
  return chunks;
};

const executeTreatmentQuery = async <T extends EntityWithId>(
  supabase: SupabaseClient,
  namesChunk: string[],
  columns: string,
  signal?: AbortSignal
): Promise<T[]> => {
  let query = supabase.from('treatments').select(columns).in('name', namesChunk);
  if (signal) {
    query = query.abortSignal(signal);
  }
  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return data as unknown as T[];
};
const deduplicateResults = <T extends EntityWithId>(chunkResults: T[][]): T[] => {
  const combined: T[] = [];
  const seenIds = new Set();

  for (const data of chunkResults) {
    for (const treatment of data) {
      if (!seenIds.has(treatment.id)) {
        seenIds.add(treatment.id);
        combined.push(treatment);
      }
    }
  }

  return combined;
};

// Types for function options
interface FetchTreatmentsOptions {
  chunkSize?: number;
  signal?: AbortSignal;
  columns?: string;
}

// Same field selection as useHomeTreatments to ensure image_urls is included
const TREATMENT_SELECT_FIELDS = [
  'id',
  'unique_code',
  'name',
  'name_translations',
  'korean_name',
  'category',
  'subcategory',
  'medical_procedure_id',
  'image_urls',
  'description',
  'description_translations',
  'cost_estimate_min',
  'cost_estimate_max',
  'duration_type',
  'duration_unit',
  'duration_value',
  'duration_range_min',
  'duration_range_max',
  'recovery_type',
  'recovery_unit',
  'recovery_value',
  'recovery_range_min',
  'recovery_range_max',
  'anesthesia_required',
  'treatment_types',
  'treatment_types_translations',
  'created_at',
].join(', ');

export const fetchTreatmentsByNames = async <T extends EntityWithId = EntityWithId>(
  supabase: SupabaseClient,
  names: unknown,
  { chunkSize = DEFAULT_CHUNK_SIZE, signal, columns = TREATMENT_SELECT_FIELDS }: FetchTreatmentsOptions = {}
): Promise<T[]> => {
  const effectiveChunkSize = Math.max(
    1,
    Number.isFinite(chunkSize) ? Number(chunkSize) : DEFAULT_CHUNK_SIZE
  );

  const normalizedNames = normalizeNames(names);
  if (normalizedNames.length === 0) {
    return [] as T[];
  }

  // For very small sets we can run a single request
  if (normalizedNames.length <= effectiveChunkSize) {
    try {
      return await executeTreatmentQuery<T>(supabase, normalizedNames, columns, signal);
    } catch {
      return [];
    }
  }

  const chunks = chunkArray(normalizedNames, effectiveChunkSize);

  const chunkResults = await Promise.all(
    chunks.map(async chunk => {
      try {
        return await executeTreatmentQuery<T>(supabase, chunk, columns, signal);
      } catch {
        return [];
      }
    })
  );

  return deduplicateResults(chunkResults);
};
